import { useRef } from "react";
import { motion, useScroll, useSpring, useTransform } from "framer-motion";
import { EASE, SectionTag, MaskedLine } from "./Motion";

const milestones = [
  {
    year: "2018",
    title: "The Bedroom HQ",
    text: "Two laptops, one panel and an obsession with delivery speed. BEATKINGSMM ships its first 10,000 orders.",
  },
  {
    year: "2019",
    title: "The First Million",
    text: "Order #1,000,000 is fulfilled. Resellers across India begin building real businesses on our rails.",
  },
  {
    year: "2021",
    title: "Brand Deals Division",
    text: "We start connecting brands directly to creators — no middlemen, transparent pricing, measurable reach.",
  },
  {
    year: "2023",
    title: "Smarter Sourcing",
    text: "A global supply arm opens: vetted suppliers, hard negotiation and white-glove operations for our clients.",
  },
  {
    year: "2025",
    title: "42 Crore Orders",
    text: "Forty-two crore orders delivered and ₹710 crore in client value facilitated. The engine hits cruising altitude.",
  },
  {
    year: "2026",
    title: "BEATKINGSMM 2.0",
    text: "The rebuild. One ecosystem — SMM, brand deals and sourcing — engineered like a global tech company.",
  },
];

export default function Timeline() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start 0.75", "end 0.65"] });
  const trace = useSpring(scrollYProgress, { stiffness: 80, damping: 22 });
  const scaleY = useTransform(trace, [0, 1], [0, 1]);

  return (
    <section id="story" data-testid="timeline-section" className="relative py-28 sm:py-36">
      <div className="mx-auto max-w-7xl px-6 sm:px-8 lg:px-16">
        <SectionTag index="02" label="Since 2018 — The Legacy" testid="timeline-tag" />
        <h2 className="mt-6 max-w-3xl text-4xl font-bold tracking-tight text-neutral-50 sm:text-5xl">
          <MaskedLine>Eight years of</MaskedLine>
          <MaskedLine delay={0.12} innerClassName="font-serif italic font-medium text-gold-grad">
            compounding.
          </MaskedLine>
        </h2>

        <div ref={ref} className="relative mt-20">
          <div className="absolute left-4 top-0 h-full w-px bg-white/10 md:left-1/2" />
          <motion.div
            style={{ scaleY }}
            className="absolute left-4 top-0 h-full w-px origin-top bg-gradient-to-b from-goldlight via-gold to-goldbright shadow-[0_0_14px_rgb(var(--gold)_/_0.5)] md:left-1/2"
            data-testid="timeline-trace"
          />

          <div className="space-y-16 md:space-y-24">
            {milestones.map((m, i) => (
              <div key={m.year} className="relative pl-14 md:grid md:grid-cols-2 md:gap-24 md:pl-0">
                <motion.span
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  viewport={{ once: true, amount: 0.8 }}
                  transition={{ duration: 0.6, ease: EASE }}
                  className="absolute left-4 top-2 h-3 w-3 -translate-x-1/2 rounded-full bg-gold ring-4 ring-gold/15 md:left-1/2"
                />
                <motion.div
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.5 }}
                  transition={{ duration: 0.9, ease: EASE }}
                  className={i % 2 ? "md:col-start-2" : "md:col-start-1 md:row-start-1 md:text-right"}
                  data-testid={`timeline-step-${m.year}`}
                >
                  <div className="font-serif text-5xl italic text-gold-grad sm:text-6xl">{m.year}</div>
                  <h3 className="mt-3 text-xl font-semibold text-neutral-100 sm:text-2xl">{m.title}</h3>
                  <p
                    className={`mt-3 max-w-md text-sm leading-relaxed text-neutral-400 sm:text-base ${
                      i % 2 ? "" : "md:ml-auto"
                    }`}
                  >
                    {m.text}
                  </p>
                </motion.div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}
